import { useEffect, useState } from "react";
import { Mail, Phone, Search } from "lucide-react";
import { api } from "../../lib/api";
import { CustomerDetailDialog } from "./CustomerDetailDialog";

const STATUS_OPTIONS = ["new", "contacted", "won", "lost"];

const STATUS_STYLES = {
  new: "bg-[#d4a840]/15 text-[#d4a840] border-[#d4a840]/40",
  contacted: "bg-blue-400/10 text-blue-300 border-blue-400/30",
  won: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30",
  lost: "bg-white/5 text-white/40 border-white/15",
};

const SOURCE_LABELS = {
  contact_form: "Contact Form",
  onboarding_dialog: "AI Onboarding",
};

function initials(name) {
  return (name || "").trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");
}

export function EnquiriesTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/leads")
      .then((res) => setLeads(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const updateStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    await api.patch(`/admin/leads/${id}`, { status }).catch(() => load());
  };

  const filtered = leads.filter((l) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [l.name, l.email, l.phone, l.company, l.business_type, l.package_title].join(" ").toLowerCase().includes(q);
  });

  return (
    <div data-testid="admin-enquiries-tab">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <div className="kb-label mb-3">Customers</div>
          <h1 className="font-display text-3xl font-bold text-white">All Enquiries</h1>
          <p className="text-white/50 text-sm mt-2">{leads.length} total, from the contact form and the AI onboarding dialog.</p>
        </div>
        <div className="relative w-full md:w-72">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, company..."
            data-testid="enquiries-search"
            className="w-full bg-[#0a1f4d]/60 border border-[#d4a840]/20 rounded-xl pl-11 pr-4 py-2.5 text-sm text-white placeholder-white/30 focus:border-[#d4a840] focus:outline-none transition-colors"
          />
        </div>
      </div>

      <div className="kb-glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-white/50 text-xs uppercase tracking-wider border-b border-white/10">
                <th className="px-5 py-4 font-medium">Customer</th>
                <th className="px-5 py-4 font-medium">Contact</th>
                <th className="px-5 py-4 font-medium">Interest</th>
                <th className="px-5 py-4 font-medium">Source</th>
                <th className="px-5 py-4 font-medium">Status</th>
                <th className="px-5 py-4 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-white/40">
                    No enquiries found.
                  </td>
                </tr>
              ) : null}
              {filtered.map((l) => (
                <tr
                  key={l.id}
                  onClick={() => setSelectedId(l.id)}
                  className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] cursor-pointer"
                  data-testid={`lead-row-${l.id}`}
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-[#d4a840]/15 border border-[#d4a840]/40 flex items-center justify-center text-xs font-semibold text-[#d4a840] shrink-0">
                        {initials(l.name)}
                      </div>
                      <div className="min-w-0">
                        <div className="text-white font-medium truncate">{l.name}</div>
                        {l.company ? <div className="text-white/40 text-xs truncate">{l.company}</div> : null}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="space-y-1">
                      {l.email ? (
                        <div className="flex items-center gap-2 text-white/70 text-xs">
                          <Mail size={12} className="text-[#d4a840]" /> {l.email}
                        </div>
                      ) : null}
                      {l.phone ? (
                        <div className="flex items-center gap-2 text-white/70 text-xs">
                          <Phone size={12} className="text-[#d4a840]" /> {l.phone}
                        </div>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-white/70 max-w-xs">
                    <div className="truncate">{l.package_title || l.service}</div>
                    {l.business_type ? <div className="text-white/40 text-xs truncate">{l.business_type}</div> : null}
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-xs text-white/60">{SOURCE_LABELS[l.source] || l.source}</span>
                  </td>
                  <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={l.status}
                      onChange={(e) => updateStatus(l.id, e.target.value)}
                      data-testid={`lead-status-${l.id}`}
                      className={`text-xs font-medium px-2.5 py-1.5 rounded-full border bg-transparent focus:outline-none capitalize ${STATUS_STYLES[l.status] || STATUS_STYLES.new}`}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s} className="bg-[#0a1330] text-white">
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-4 text-white/50 text-xs whitespace-nowrap">
                    {new Date(l.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CustomerDetailDialog
        lead={leads.find((l) => l.id === selectedId) || null}
        onClose={() => setSelectedId(null)}
        onChanged={load}
      />
    </div>
  );
}
